import { CurrencyPipe } from '@angular/common';
import { Injectable } from '@angular/core';
import { ConstitutionDetailDataResponse, DetailConstitution, JSONMatrixBienes, Partner } from 'src/app/shared/models/constitution/client-constitucion.model';
import { ConstitutionInterface, FlatRecordConstitution } from 'src/app/shared/models/constitution/constitution.model';
import { KanbanCardBase } from 'src/app/shared/models/util/kanban.model';
import {
    CCPJ_STEPS_DICTIONARY,
    STANDARD_STATUSES_CONTACT_CENTER_DICTIONARY,
    STATUS_NOTARIZA_DICTIONARY,
    STATUSES_PROCESS_CONSTITUTION,
} from 'src/app/shared/constants/crece-digital.constants';
import { DOMAIN_TYPE_APPLICATION } from 'src/app/shared/models/documents/documents.model';

@Injectable({
    providedIn: 'root',
})

export class ConstitutionMapperService {

    private readonly currencyPipe = new CurrencyPipe('es-PE');

    private parseCustomDate(dateStr: string): Date | undefined {
        if (!dateStr) return undefined;
        // Formato nuevo del back: "2026-09-17T11:22:18.049011" (hora local) o "2026-09-17".
        if (/^\d{4}-\d{2}-\d{2}/.test(dateStr)) {
            const isoDate = dateStr.length === 10 ? this.parseLocalDate(dateStr) : new Date(dateStr);
            return Number.isNaN(isoDate.getTime()) ? undefined : isoDate;
        }
        // Ejemplo: "27/04/2025, 10:22:42"
        const [datePart, timePart] = dateStr.split(',');
        if (!datePart || !timePart) return undefined;
        const [day, month, year] = datePart.trim().split('/');
        const [hour, minute, second] = timePart.trim().split(':');
        return new Date(
            Number(year),
            Number(month) - 1,
            Number(day),
            Number(hour),
            Number(minute),
            Number(second)
        );
    }


    /**
     * `matchStatus` es el `statusDefault` de la pestaña que pidió estos registros: el back
     * filtra `estadoNotaria` server-side pero no siempre lo devuelve en cada fila, así que
     * es una clave de match más confiable que `r?.estadoNotaria` para `latestMatchingComment`.
     */
    mapRecords(records: any[], typeVariant = 0, matchStatus?: string): ConstitutionInterface[] {
        return records?.map((r): ConstitutionInterface => {
            const notaryStatus = r?.estadoNotaria || '';
            const rawCommentHistory = this.parseCommentHistory(r?.historialComentarios);
            return {
                recordID: r?.recordID,
                notaryStatus,
                name: r?.nombre,
                phone: r?.celular,
                registerDate: this.parseCustomDate(r?.fechaCreacion),
                lastStateUpdateDate: r?.ultimaModificacionNotaria
                    ? new Date(r.ultimaModificacionNotaria)
                    : undefined,
                startDateConstitution: this.parseCustomDate(r?.fechaInicioConstitucion),
                // Lee directo el nombre que manda el back.
                attachments: r?.adjuntos || [],
                attachmentsRuc: r?.adjuntarDocRuc || [],
                numberReservationName: r?.numeroTituloReservaNombre || '',
                registrationTitleNumber: r?.numeroTituloInscripcion || '',
                tickets: r?.tickets || [],
                ticketsToNotary: r?.ticketsResolverNotaria || [],
                draftMinute: r?.minutaBorrador || [],
                minuteModifications: r?.modificacionesMinuta || '',
                email: r?.correo || '',
                finalMinute: r?.minutaFinal || [],
                notary: r?.notaria || '',
                processStatus: r?.estadoProcesoConstitucion || '',
                creceFollowupStatus: r?.estadoCreceFollowup,
                paymentVoucherBcp: r?.comprobantePDF || [],
                secondPayAmount: r?.costoPendienteSugerido,
                firstPaymentVoucher: r?.voucherPrimerPago || [],
                secondpaymentVoucher: r?.voucherSegundoPago || [],
                secondPayValidated: this.toBooleanFlag(r?.segundoPagoVoucherValidado),
                managedBombero: this.toBooleanFlag(r?.gestionadoBombero),
                capitalVoucher: r?.voucherCapital || [],
                hasRuc: r?.tieneRuc || '',
                finalDocuments: r?.documentosFinales || [],
                ruc: r?.ruc,
                finalSignatureDate: r?.fechaFirmaFinal || undefined,
                AgendaSignatureSprintful: r?.agendaFirmaSprintFul || [],
                reAgendaSignature: r?.reagendaFechaFirma
                    ? this.parseLocalDate(r.reagendaFechaFirma)
                    : undefined,
                totalCountsTickets:  r.numberOfTickets,
                commentHistory: rawCommentHistory,
                // El modal guarda 'Sin categorizar' cuando no hay estadoNotaria — se
                // empareja con la misma etiqueta, no con ''.
                latestMatchingComment: this.extractLatestComment(rawCommentHistory, matchStatus || notaryStatus || 'Sin categorizar', typeVariant) ?? undefined,
                validationANGeneral: r?.validacionANGeneral,
                creceID: r?.creceID,
                typeConstitution: r?.tipoConstitucion,
                dateCreationFollowUp: this.parseCustomDate(r?.fechaCreacion),
                companyType: r?.tipoEmpresaAbreviado,
                contactEvaluation: r?.evaluacionContacto,
                firstVoucherValidated: this.toBooleanFlag(r?.primerPagoVoucherValidado),
                commentsFollowup: r?.comentariosFollowup,
                businessDaysSinceLastModification: r?.diasHabilesDesdeUltimaModificacion,
            };
        });
    }

    parseLocalDate(dateString: string): Date {
        const [year, month, day] = dateString.split('-').map(Number);
        return new Date(year, month - 1, day);
    }


    /** Normaliza `historialComentarios`: puede llegar como array o como JSON string. */
    private parseCommentHistory(raw: unknown): any[] {
        if (Array.isArray(raw)) return raw;
        if (typeof raw !== 'string') return [];

        try {
            const parsed = JSON.parse(raw);
            return Array.isArray(parsed) ? parsed : [];
        } catch {
            return []; // invalid JSON
        }
    }

    private extractLatestComment(entries: any[], notaryStatus: any, typeVariant: number): any {

        if (typeVariant == 0) if (!entries.length || !notaryStatus) return null;

        if (typeVariant == 1) if (!entries.length) return null;

        const targetStatus = String(notaryStatus).trim();
        let latestEntry: any = null;
        let latestTimestamp = -Infinity;

        for (const entry of entries) {
            if (!entry || typeof entry !== "object") continue;

            const status = String(entry.status ?? "").trim();
            if (status !== targetStatus) continue;

            const ts = Date.parse(entry.date);
            if (!Number.isFinite(ts)) continue;

            if (ts > latestTimestamp) {
                latestTimestamp = ts;
                latestEntry = entry;
            }
        }

        return latestEntry;
    }

    mapConstitutionToKanbanRegularizacion(records: FlatRecordConstitution[]): KanbanCardBase[] {
        return records.map((r: any) => {
            const record = r.fields ?? r;

            return {
                id: record.recordID ?? r.id,
                recordId: record.recordID ?? r.id,
                estado: this.toCcpjColumn(record.estadoRegularizacionCCPJdigital),
                registerDate: this.parseCustomDate(record.fechaCreacion),
                lastStateUpdateDate: record.ultimaModificacionRegularizacionCCPJ
                    ? new Date(record.ultimaModificacionRegularizacionCCPJ)
                    : undefined,
                consultationDate: record.ultimaModificacionRegularizacionCCPJ
                    ? new Date(record.ultimaModificacionRegularizacionCCPJ)
                    : undefined,
                matchedFields: r.matchedFields ?? [],
                name: record.nombre,
                phone: record.celular,
                companyName: record.nombreReservado,
                email: record.correo,
                dni: record.dni,
                creceID: record.creceID,
                flowType: record.tipoFlujo === 'Estandar' ? 'Estándar' : record.tipoFlujo
            };
        });
    }

    /** El back manda estos flags como el texto "true"/"false", no como booleano. */
    private toBooleanFlag(value: unknown): boolean {
        const text = typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
            ? String(value)
            : '';
        return text.trim().toLowerCase() === 'true';
    }

    /** Igual que `toBooleanFlag()`, pero para pintar con `DataUtil.tieneNombreReservado`. */
    private toYesNoLabel(value: unknown): string {
        if (value === undefined || value === null || value === '') return '';
        return this.toBooleanFlag(value) ? 'Sí' : 'No';
    }

    /**
     * Columna del kanban de regularización. El back manda el enum y la columna se identifica
     * por su etiqueta; vacío o nulo cae en "Sin categorizar".
     */
    private toCcpjColumn(rawStatus: unknown): string {
        const status = typeof rawStatus === 'string' ? rawStatus.trim() : '';
        if (!status) return 'Sin categorizar';

        return CCPJ_STEPS_DICTIONARY[status] ?? status;
    }

    mapToConstitutionDetail(record: Record<string, any>): DetailConstitution {
        const rawCommentHistory = this.parseCommentHistory(record["historialComentarios"]);
        const clientData: DetailConstitution = {
            fullName: record["nombre"] ?? "-",
            recordId: record["recordID"] ?? "-",
            dni: record["dni"] ?? "-",
            notary: record["notaria"] ?? "-",
            email: record["correo"] ?? "-",
            status: record["estadoCreceFollowup"] ?? "-",
            signatureMode: record["modalidadFirma"] ?? "-",
            companyType: record["tipoEmpresaAbreviado"] ?? "-",
            phoneNumber: record["celular"] ?? "-",
            addressHome: record["domicilio"] ?? "-",
            district: record["distritoCrece"] ?? "-",
            fiscalAddress: record["direccionFiscalDireccion"] ?? "-",
            statusConstitucion: record["estadoNotaria"],
            tickets: record["tickets"] ?? [],
            ticketsNotary: record["ticketsResolverNotaria"] ?? [],
            numberOfPartners: record["numeroSocios"] ?? "-",
            capitalRange: record["rangoCapital"] ?? "-",
            capitalRangeType: record["tipoRangoCapital"] ?? "-",
            totalCapital: record["totalMontoCapitalSocial"] == null
                ? 'S/ 0.00'
                : `S/ ${this.currencyPipe.transform(
                    record["totalMontoCapitalSocial"],
                    'PEN',
                    '',
                    '1.2-2'
                )}`,
            contributionType: record["tipoDeAporte"] ?? "-",
            businessSector: record["rubroCompletadoAI"] ?? "",
            fiscalDistrict: record["direccionFiscalDistrito"] ?? "-",
            fiscalProvince: record["direccionFiscalProvincia"] ?? "-",
            fiscalDepartment: record["direccionFiscalDepartamento"] ?? "-",
            optionalName1: record["nombreOpcional1"] ?? "-",
            optionalName2: record["nombreOpcional2"] ?? "-",
            optionalName3: record["nombreOpcional3"] ?? "-",
            reservedName: record["nombreReservado"] ?? "",
            reservedTitleNumber: record["numeroTituloReservaNombre"] ?? "-",
            draftMinuteDocument: record["minutaBorrador"] ?? [],
            draftMinuteDocumentWord: record["minutaBorradorWord"] ?? [],
            minuteModifications: record["modificacionesMinuta"] ?? "",
            finalMinute: record["minutaFinal"] ?? [],
            automatedMinute: record["minutaAutomatizada"] ?? [],
            ruc: record["ruc"] ?? "",
            finalDocuments: record["documentosFinales"] ?? [],
            rucFile: record["fichaRuc"] ?? [],
            firstPayment: record["primerPago"] ?? 0,
            suggestedPendingCost: record["costoPendienteSugerido"],
            additionalPayments: record["pagosAdicionales"] == null
                ? ""
                : record["pagosAdicionales"].toLocaleString('en-US').trim(),
            capitalVoucher: record["voucherCapital"] ?? [],
            form5340: record["formulario5340"] ?? [],
            fichaRuc: record["fichaRuc"] ?? [],
            fileFatca: record["declaracionFacta"] ?? [],
            openedAccountIn: record["abrioCuentaEn"] ?? "",
            attachments: record["adjuntos"] ?? [],
            firstPaymentVoucher: record["voucherPrimerPago"] ?? [],
            secondPaymentVoucher: record["voucherSegundoPago"] ?? [],
            accountNumber: record["numeroCuenta"] ?? "",
            estimatedConstitutionCost: record["costoConstitucionEstimado"] ?? 0,
            totalPayment: record["pagoTotal"] ?? 0,
            inscriptionTitleNumber: record["numeroTituloInscripcion"] ?? "",
            additionalPaymentsAttachments: record["pagosAdicionalesAdjuntos"] ?? [],
            digitalTestimony: record["testimonioDigital"] ?? [],
            inscriptionRecord: record["asientoInscripcion"] ?? [],
            hasRuc: record["tieneRuc"] ?? "",
            rucObservation: record["observacionRuc"] ?? "",
            automatedMinutePDF: record["minutaAutomatizadaPDF"] ?? [],
            automatedMinuteWord: record["minutaAutomatizadaWord"] ?? [],
            rucAttachment: record["adjuntarDocRuc"] ?? [],
            documentsFinals: record["documentosFinales"] ?? [],
            extraPayments: record["pagosExtras"],
            hasNameReserved: record["tieneNombreRazonSocialReservado"] ?? "",
            lastUpdatedStatusFollowup: record["ultimaModificacionFollowup"]
                ? new Date(record["ultimaModificacionFollowup"])
                : undefined,
            lastUpdatedStatusNotary: record["ultimaModificacionNotaria"]
                ? new Date(record["ultimaModificacionNotaria"])
                : undefined,
            reservationDate: record["fechaReservaNombre"]
                ? new Date(record["fechaReservaNombre"] + 'T00:00:00')
                : undefined,
            lastUpdatedRegister: record["ultimaModificacionRegistro"]
                ? new Date(record["ultimaModificacionRegistro"])
                : undefined,
            partners: [],
            ccpjFecha: record["ccpjFecha"] ? new Date(record["ccpjFecha"]) : undefined,
            ultimaModificacionRegularizacionCCPJ: record["ultimaModificacionRegularizacionCCPJ"] ?? undefined,
            bienesConstitucion: [],
            businessDayIncorporation: record["diasHabilesConstitucion"] ?? '-',
            typeConstitution: record["tipoConstitucion"] ?? '-',
            statusProcessConstitution: record["estadoProcesoConstitucion"] ?? '-',
            dateCreationFollowUp: this.parseCustomDate(record["fechaCreacion"]),
            dateCreation: this.parseCustomDate(record["fechaCreacion"]) ?? '',
            scheduleSprintFul: record["agendaFirmaSprintFul"] ?? '',
            flowType: record["tipoFlujo"] ?? '-',
            capitalRangeTypeNumber: record["tipoRangoCapital"] ?? '-',
            contactEvaluation: record["evaluacionContacto"] ?? '-',
            additionalBusiness: record["rubrosAdicionales"] ?? '',
            agendaFirmaSprintFul: record["agendaFirmaSprintFul"] ?? [],
            firstVoucherValidated: this.toBooleanFlag(record['primerPagoVoucherValidado']),
            firstVoucher: record['voucherPrimerPago'] ?? '',
            commentsFollowup: record['comentariosFollowup'] ?? '',
            commitmentToPay: record['compromisoPago'] ?? '',
            commentStatusProcess: record['comentarioEstadoProceso'] ?? '',
            conversationClient: record['consultaCliente'] ?? '',
            isObserved: record['validacionANGeneral'] ?? '',
            commentHistory: rawCommentHistory,
            regularizationEntry: record['anotacionesRegularizacionCCPJ'] ?? '',
            b2cId: record['b2cId'] ?? '',
            creceID: record['creceID'] ?? '',
            ultimeUsedCoupon: record['ultimeUsedCoupon'] ?? '',
        };

        for (let i = 1; i <= 5; i++) {
            const jsonMatrixBienesRaw = record[`s${i}JSONMatrixBienes`] ?? '[]';
            let jsonMatrixBienes: JSONMatrixBienes[] = [];

            try {
                jsonMatrixBienes = JSON.parse(jsonMatrixBienesRaw);
            } catch {
                // ignora JSON inválido
            }

            const cash = record[`s${i}MontoAporte`];
            const goods = record[`s${i}MontoAporteBienes`];

            const partner: Partner = {
                recordIdPartner: record[`s${i}RecordIdSocio`] ?? "",
                name: record[`s${i}NombreCompleto`] ?? "",
                documentType: record[`s${i}TipoDocumento`] ?? "",
                documentNumber: record[`s${i}NumeroDocumento`] ?? "",
                nationality: record[`s${i}Nacionalidad`] ?? "",
                maritalStatus: record[`s${i}EstadoCivil`] ?? "",
                occupation: record[`s${i}Ocupacion`] ?? "",
                spouseDocumentType: record[`s${i}TipoDocumentoConyuge`] ?? null,
                spouseDocumentNumber: record[`s${i}NumeroDocumentoConyuge`] ?? null,
                spouseOccupation: record[`s${i}OcupacionConyuge`] ?? null,
                mainPartnerRole: record[`s${i}CargoOcupacion`] ?? "",
                documentsPartner: record[`s${i}DocumentoDniCe`] ?? [],
                documentsSpouse: record[`s${i}DocumentoConyugeDniCe`] ?? [],
                marriageCertificate: record[`s${i}DocumentoConyugeActaMatrimonio`] ?? [],
                typeContribution: record[`s${i}TipoAporteCapital`] ?? null,
                cashContribution: cash == null ? undefined : Number(cash),
                goodsContribution: goods == null ? undefined : Number(goods),
                totalContribution:
                    (cash == null ? 0 : Number(cash)) +
                    (goods == null ? 0 : Number(goods)),
                JSONMatrixBienes: jsonMatrixBienes,
                RegistryEntry: record[`s${i}PartidaRegistral`] ?? "",
                SeparationOfAssetsDocument: record[`s${i}DocumentoConyugeSeparacionBienes`] ?? [],
            };

            if (partner.name) {
                clientData.partners!.push(partner);
            }

            clientData.bienesConstitucion =
                clientData.bienesConstitucion!.concat(jsonMatrixBienes);
        }

        return clientData;
    }

    /**
     * Mapea la respuesta de `GET api/constitution/detail/{recordId}`. El back ya devuelve los
     * nombres finales: acá solo se normalizan fechas, estados y socios.
     */
    /** Campos del detalle que se renderizan con `<app-file-preview-small>`. */
    private static readonly ATTACHMENT_FIELDS: (keyof DetailConstitution)[] = [
        'draftMinuteDocument', 'draftMinuteDocumentWord', 'finalMinute', 'attachments',
        'automatedMinuteWord', 'automatedMinutePDF', 'rucAttachment', 'documentsFinals',
        'digitalTestimony', 'inscriptionRecord', 'additionalPaymentsAttachments',
        'firstPaymentVoucher', 'secondPaymentVoucher', 'capitalVoucher',
        'form5340', 'fichaRuc', 'fileFatca', 'finalDocuments', 'rucFile',
    ];

    /** Mismos campos, del lado de cada socio. */
    private static readonly PARTNER_ATTACHMENT_FIELDS = [
        'documentsPartner', 'SeparationOfAssetsDocument', 'marriageCertificate', 'documentsSpouse',
    ] as const;

    /**
     * `FilePreviewSmallComponent` necesita `domainType` junto a `file.id` para
     * resolver la URL firmada de descarga; el back no lo manda en el detalle.
     */
    private withDomainType(files: any[] | undefined): any[] | undefined {
        return files?.map(file => (file?.id
            ? { ...file, domainType: file.domainType ?? DOMAIN_TYPE_APPLICATION }
            : file));
    }

    private attachDomainType(detail: DetailConstitution): DetailConstitution {
        const patched: any = { ...detail };

        for (const field of ConstitutionMapperService.ATTACHMENT_FIELDS) {
            patched[field] = this.withDomainType(detail[field] as any[] | undefined);
        }

        patched.partners = detail.partners?.map(partner => {
            const patchedPartner: any = { ...partner };
            for (const field of ConstitutionMapperService.PARTNER_ATTACHMENT_FIELDS) {
                patchedPartner[field] = this.withDomainType(partner[field]);
            }
            return patchedPartner;
        });

        return patched;
    }

    mapToConstitutionDetailStandard(response: ConstitutionDetailDataResponse): DetailConstitution {
        const partners = this.mapToConstitutionDetailPartners(response.partners ?? []);

        return this.attachDomainType({
            ...response,
            statusConstitucion: STATUS_NOTARIZA_DICTIONARY[response.statusConstitucion as string]
                ?? response.statusConstitucion,
            status: STANDARD_STATUSES_CONTACT_CENTER_DICTIONARY[response.status as string]
                ?? response.status,
            statusProcessConstitution: STATUSES_PROCESS_CONSTITUTION[response.statusProcessConstitution as string]
                ?? response.statusProcessConstitution,
            totalCapital: response.totalCapital == null
                ? 'S/ 0.00'
                : `S/ ${this.currencyPipe.transform(response.totalCapital, 'PEN', '', '1.2-2')}`,
            lastUpdatedStatusFollowup: this.toDate(response.lastUpdatedStatusFollowup),
            lastUpdatedStatusNotary: this.toDate(response.lastUpdatedStatusNotary),
            lastUpdatedRegister: this.toDate(response.lastUpdatedRegister),
            ccpjFecha: this.toDate(response.ccpjFecha),
            ultimaModificacionRegularizacionCCPJ: this.toDate(response.ultimaModificacionRegularizacionCCPJ),
            dateCreationFollowUp: this.toDate(response.dateCreationFollowUp),
            // Llega como fecha sin hora; se ancla al día local para no correrla por zona horaria.
            reservationDate: this.toDate(response.reservationDate, true),
            hasNameReserved: this.toYesNoLabel(response.hasNameReserved),
            partners,
            // Ya no viene del back: se deriva de los bienes declarados por cada socio.
            bienesConstitucion: partners.flatMap(p => p.JSONMatrixBienes ?? []),
        });
    }

    mapToConstitutionDetailPartners(partners: Partner[]): Partner[] {
        return partners.map(partner => {
            const raw = partner as any;

            return {
                ...partner,
                mainPartnerRole: this.extractMainPartnerRole(partner.mainPartnerRole),
                JSONMatrixBienes: this.extractPartnerGoods(partner),
                RegistryEntry: raw.registryDocument ?? partner.RegistryEntry,
                SeparationOfAssetsDocument: raw.separacionBienes ?? partner.SeparationOfAssetsDocument,
            };
        });
    }

    /** `mainPartnerRole` llega como JSON serializado o como arreglo; interesa el primer cargo. */
    private extractMainPartnerRole(rawRole: unknown): string | undefined {
        let roles: any[] = [];

        if (Array.isArray(rawRole)) {
            roles = rawRole;
        } else if (typeof rawRole === 'string') {
            try {
                const parsed = JSON.parse(rawRole);
                roles = Array.isArray(parsed) ? parsed : [];
            } catch {
                // No es JSON: se asume que ya es el nombre del cargo.
                return rawRole;
            }
        }

        return roles[0]?.roleName ?? undefined;
    }

    /** El back manda el arreglo de bienes del socio en `jsonMatrixBienes`. */
    private extractPartnerGoods(partner: any): JSONMatrixBienes[] {
        const raw = partner?.jsonMatrixBienes ?? [];

        if (Array.isArray(raw)) return raw;

        if (typeof raw === 'string') {
            try {
                const parsed = JSON.parse(raw);
                return Array.isArray(parsed) ? parsed : [];
            } catch {
                return [];
            }
        }

        return [];
    }

    private toDate(value?: string, dateOnly = false): Date | undefined {
        if (!value) return undefined;

        const date = new Date(dateOnly ? `${value}T00:00:00` : value);
        return Number.isNaN(date.getTime()) ? undefined : date;
    }
}
